import { useEffect, useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as Clipboard from 'expo-clipboard';
import { useAuth } from '../context/AuthContext';
import { getReferralProgress, ReferralProgress } from '../api/users';
import { GradientCard } from './ui';
import { colors, fonts, gradients } from '../theme';

function ProgressBar({ percent }: { percent: number }) {
  return (
    <View style={{ height: 6, borderRadius: 3, backgroundColor: 'rgba(255,255,255,0.28)', overflow: 'hidden' }}>
      <View
        style={{
          height: '100%',
          width: `${Math.min(100, Math.max(0, percent))}%`,
          borderRadius: 3,
          backgroundColor: '#fff',
        }}
      />
    </View>
  );
}

function ProgressRow({ label, count, cap, rewardCoins }: { label: string; count: number; cap: number; rewardCoins: number }) {
  const remaining = Math.max(0, cap - count);
  return (
    <View style={{ marginTop: 12 }}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 6 }}>
        <Text style={{ color: '#fff', fontFamily: fonts.semiBold, fontSize: 13 }}>{label}</Text>
        <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12 }}>
          {remaining > 0 ? `Còn ${remaining} suất · +${rewardCoins} xu` : 'Đã hết suất'}
        </Text>
      </View>
      <ProgressBar percent={(count / cap) * 100} />
    </View>
  );
}

/** Platform-wide "quest" — invite new shop owners / task-doers to Loot,
 * capped and shared across all users (see getReferralProgress). Surfaced as
 * its own task-like card so a brand-new user with no nearby shop tasks yet
 * still has something to do right away. */
export function PlatformTaskCard() {
  const { token, user } = useAuth();
  const [progress, setProgress] = useState<ReferralProgress | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!token) return;
    getReferralProgress(token)
      .then(setProgress)
      .catch(() => {
        // Silent — this is a bonus card, not core functionality.
      });
  }, [token]);

  if (!user?.referralCode) return null;
  const bothFull = progress ? progress.shopOwner.full && progress.reviewer.full : false;

  const copyReferralCode = async () => {
    await Clipboard.setStringAsync(user.referralCode ?? '');
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <GradientCard colors={gradients.primary} style={{ marginBottom: 20 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <Ionicons name="megaphone" size={14} color="#fff" />
        <Text style={{ color: '#fff', fontFamily: fonts.bold, fontSize: 12, letterSpacing: 0.4 }}>
          NHIỆM VỤ NỀN TẢNG
        </Text>
      </View>
      <Text style={{ fontSize: 17, fontFamily: fonts.bold, color: '#fff' }}>Mời người mới tham gia Loot</Text>
      <Text style={{ color: 'rgba(255,255,255,0.85)', marginTop: 4, fontSize: 13, lineHeight: 19 }}>
        Chia sẻ mã của bạn — mỗi chủ shop hoặc người làm nhiệm vụ mới tham gia thành công (đã xác minh và hoạt động thật) đều mang về xu cho bạn.
      </Text>

      {bothFull ? (
        <Text style={{ color: '#fff', marginTop: 14, fontSize: 13, fontFamily: fonts.semiBold }}>
          Đợt thưởng mời hiện đã đủ suất — cảm ơn bạn đã đồng hành cùng Loot!
        </Text>
      ) : progress ? (
        <>
          <ProgressRow
            label="Chủ shop mới"
            count={progress.shopOwner.count}
            cap={progress.shopOwner.cap}
            rewardCoins={progress.shopOwner.rewardCoins}
          />
          <ProgressRow
            label="Người làm nhiệm vụ mới"
            count={progress.reviewer.count}
            cap={progress.reviewer.cap}
            rewardCoins={progress.reviewer.rewardCoins}
          />
        </>
      ) : null}

      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: 16,
          backgroundColor: 'rgba(255,255,255,0.16)',
          borderRadius: 12,
          paddingVertical: 10,
          paddingHorizontal: 14,
        }}
      >
        <Text style={{ color: '#fff', fontFamily: fonts.bold, fontSize: 15, letterSpacing: 1 }}>
          {user.referralCode}
        </Text>
        {!bothFull ? (
          <Pressable
            onPress={copyReferralCode}
            style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}
            hitSlop={8}
          >
            <Ionicons name={copied ? 'checkmark' : 'copy-outline'} size={15} color="#fff" />
            <Text style={{ color: '#fff', fontFamily: fonts.semiBold, fontSize: 13 }}>
              {copied ? 'Đã sao chép' : 'Sao chép'}
            </Text>
          </Pressable>
        ) : null}
      </View>
    </GradientCard>
  );
}
